import { FC, useCallback, useEffect, useState } from 'react';

import {
  DashboardRole,
  getDashboardSummary,
  getDashboardSummaryDetail,
} from '@shared/api/giftOrder/giftOrder.controller';
import {
  DashboardSummaryResponse,
  SummaryTypeSlice,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';
import { LowerRole } from '@shared/types';
import { formatPrice } from '@shared/utils/formatPrice';

import { fmtPercent, fmtPeriod } from '../utils/dashboard.utils';
import { exportSummaryDetailExcel } from '../utils/dashboardExcel';
import { DashboardFulfillmentSection } from './DashboardFulfillmentSection';
import { CenterLoading, EmptyBox, ErrorBox } from './DashboardShared';

/** summary-detail 엔드포인트는 아직 백엔드에 없다. 404는 장애가 아니라 '미배포'다. */
const isNotFound = (e: unknown) =>
  (e as { response?: { status?: number } })?.response?.status === 404;

export const DashboardSummaryTab: FC<{
  from: string;
  to: string;
  role?: DashboardRole;
}> = ({ from, to, role }) => {
  const [data, setData] = useState<DashboardSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getDashboardSummary(from, to, role));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '데이터를 불러올 수 없습니다.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 화면 숫자는 요약탭 응답이 이미 갖고 있지만, 건별로 펼치려면 주문 단위 응답이 따로 필요하다.
  // 저장 버튼을 누를 때만 부른다 — 탭을 열 때마다 주문 전량을 받아올 이유가 없다.
  const [excelLoading, setExcelLoading] = useState(false);
  const handleExport = useCallback(async () => {
    setExcelLoading(true);
    try {
      const detail = await getDashboardSummaryDetail(from, to, role);
      const d = new Date(from);
      exportSummaryDetailExcel(
        detail,
        `${d.getFullYear()}년${d.getMonth() + 1}월`,
        role === LowerRole.BugoAgencyAdmin,
      );
    } catch (e: unknown) {
      alert(
        isNotFound(e)
          ? '건별 엑셀은 백엔드 배포 후 사용할 수 있습니다.'
          : '건별 데이터를 불러올 수 없습니다.',
      );
    } finally {
      setExcelLoading(false);
    }
  }, [from, to, role]);

  if (loading) return <CenterLoading />;
  if (error) return <ErrorBox message={error} onRetry={fetchData} />;
  if (!data) return <EmptyBox />;

  const bp = data.byPaymentDate;

  // 이체환불(수기이체) 분해는 우리가 외부 장부와 대사하려고 만든 값이라, 상조 관리자에겐 숨긴다.
  const showManualRefund = role !== LowerRole.BugoAgencyAdmin;

  // 상조마다 수수료율이 다르고 Rebate 방식 상조는 수수료가 0이라, 단일 상수로 표기할 수 없다.
  // 집계된 수수료를 기준금액으로 나눠 실효요율을 역산한다 (분모 0이면 서버 상수로 폴백).
  const effectiveFeeRate =
    bp.netCashForServiceFee > 0
      ? bp.serviceFee / bp.netCashForServiceFee
      : data.constants.serviceFeeRate;

  // 총매출·취소액은 백엔드 반영 후에만 내려온다. 구서버면 순매출 한 줄만 남는다.
  // 순매출(grossSales)은 이미 취소가 차감된 값이라 항상 존재한다.
  const tier =
    bp.grossPayment != null && bp.cancelAmount != null
      ? { grossPayment: bp.grossPayment, cancelAmount: bp.cancelAmount }
      : null;

  // 상품 유형은 쿠폰형·배송형 둘뿐. `기타`는 상품이 아니라 잔차(소량주문 추가금)라
  // 0이면 열을 만들지 않는다 → 백엔드가 배송형에 귀속시키면 자동으로 2열이 된다.
  const byType = data.salesByType;
  const typeCols = byType
    ? [
        { key: 'coupon', label: '쿠폰형', slice: byType.coupon },
        { key: 'delivery', label: '배송형', slice: byType.delivery },
        ...(byType.etc.grossPayment || byType.etc.cancelAmount || byType.etc.netSales
          ? [{ key: 'etc', label: '기타', slice: byType.etc }]
          : []),
      ]
    : [];

  // 수량은 라인 단위라 주문 건수와 다른 물건이다.
  const qtyTotals = typeCols.length
    ? typeCols.reduce(
        (t, c) => ({
          quantity: t.quantity + c.slice.quantity,
          cancelQuantity: t.cancelQuantity + c.slice.cancelQuantity,
          netQuantity: t.netQuantity + c.slice.netQuantity,
        }),
        { quantity: 0, cancelQuantity: 0, netQuantity: 0 },
      )
    : null;

  // ── 파생 지표: 전부 위 값들로 프론트에서 계산한다 (백엔드 추가 불필요) ──
  const ratio = (num: number, den: number) => (den > 0 ? num / den : 0);

  /** 평균 단가 = 순매출 ÷ 순수량. 수량 개념이 없는 `기타`(잔차)는 분자·분모에서 뺀다. */
  const unitPriceOf = (s: SummaryTypeSlice) =>
    s.netQuantity ? Math.round(s.netSales / s.netQuantity) : null;
  const totalUnitPrice = qtyTotals?.netQuantity
    ? Math.round(
        typeCols
          .filter((c) => c.slice.netQuantity)
          .reduce((sum, c) => sum + c.slice.netSales, 0) / qtyTotals.netQuantity,
      )
    : null;

  /** 순매출 구성비. `기타` 열까지 포함하므로 합이 100%가 된다. */
  const shareOf = (s: SummaryTypeSlice) => ratio(s.netSales, bp.grossSales);

  const cancelRateByAmount = tier ? ratio(tier.cancelAmount, tier.grossPayment) : null;
  const cancelRateByQuantity = qtyTotals
    ? ratio(qtyTotals.cancelQuantity, qtyTotals.quantity)
    : null;
  /** 객단가 = 순매출 ÷ 총 주문 수 */
  const avgOrderValue = bp.orderCount ? Math.round(bp.grossSales / bp.orderCount) : null;
  const taxFreeShare = ratio(bp.netTaxFreeSales, bp.grossSales);

  return (
    <div className="space-y-6">
      {/* 매출·수수료 */}
      <section className="rounded-lg bg-white p-6 shadow">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-800">
              매출 구성 요약
              <span className="ml-2 text-xs font-normal text-gray-400">
                결제일 기준 ({fmtPeriod(from, to)})
              </span>
            </h2>
            <p className="mt-1 text-xs text-gray-400">
              총매출은 해당 기간에 결제된 금액이라 바뀌지 않습니다. 취소는 구매 후 1년까지
              가능하므로, 지난 기간의 취소액과 순매출은 나중에 달라질 수 있습니다.
            </p>
          </div>
          <button
            onClick={handleExport}
            disabled={excelLoading}
            className="shrink-0 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            {excelLoading ? '저장 중…' : '건별 엑셀'}
          </button>
        </div>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch">
          <div className="lg:w-44 lg:shrink-0">
            <div className="h-full rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
              <p className="text-xs font-medium text-gray-500">총 주문</p>
              <p className="mt-1.5 text-xl font-bold text-gray-900">
                {bp.orderCount}
                <span className="ml-0.5 text-sm font-medium">건</span>
              </p>
              {bp.canceledOrderCount != null && (
                <dl className="mt-2 space-y-0.5 border-t border-gray-200 pt-2 text-xs">
                  <div className="flex justify-between">
                    <dt className="text-gray-400">전체취소</dt>
                    <dd className="font-medium tabular-nums text-gray-600">
                      {bp.canceledOrderCount}건
                    </dd>
                  </div>
                  {/* 부분취소 건수는 백엔드 미구현 — 내려오면 자동으로 나타난다 */}
                  {bp.partialCanceledOrderCount != null && (
                    <div className="flex justify-between">
                      <dt className="text-gray-400">부분취소</dt>
                      <dd className="font-medium tabular-nums text-gray-600">
                        {bp.partialCanceledOrderCount}건
                      </dd>
                    </div>
                  )}
                </dl>
              )}
            </div>
          </div>

          {/* 상품유형별 카드 + 합계. 색은 회색 한 계열로 두고 명도·굵기로만 위계를 준다.
              인디고는 마지막 합계 카드에만 써서 시선이 결론으로 가게 한다. */}
          <div className="grid min-w-0 flex-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {typeCols.map((c) => (
              <div
                key={c.key}
                className="rounded-lg border border-gray-200 bg-white px-4 py-3"
              >
                <p className="text-xs font-semibold text-gray-600">{c.label}</p>
                <dl className="mt-2 space-y-1 text-sm">
                  <div className="flex items-baseline justify-between">
                    <dt className="text-gray-500">매출</dt>
                    <dd className="font-medium tabular-nums text-gray-700">
                      {formatPrice(c.slice.grossPayment)}
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <dt className="text-gray-500">취소</dt>
                    <dd className="font-medium tabular-nums text-gray-400">
                      {c.slice.cancelAmount
                        ? `-${formatPrice(c.slice.cancelAmount)}`
                        : '-'}
                    </dd>
                  </div>
                  {/* 취소액 중 우리가 직접 이체한 몫. 이번 기간에 이체환불이 하나라도 있으면
                      값이 0인 카드도 빈 줄로 자리를 잡아 세 카드의 순매출 위치를 맞춘다.
                      (전액포인트 가상계좌 취소 탓에 etc는 음수일 수 있어 !== 0으로 본다) */}
                  {showManualRefund && bp.cancelAmountManual > 0 && (
                    <div className="flex items-baseline justify-between text-[11px]">
                      {c.slice.cancelAmountManual !== 0 ? (
                        <>
                          <dt className="text-gray-400">└ 이체환불</dt>
                          <dd className="tabular-nums text-gray-400">
                            {`-${formatPrice(c.slice.cancelAmountManual)}`}
                          </dd>
                        </>
                      ) : (
                        <dt className="text-gray-300">&nbsp;</dt>
                      )}
                    </div>
                  )}
                  <div className="flex items-baseline justify-between border-t border-gray-100 pt-1.5">
                    <dt className="font-semibold text-gray-700">순매출</dt>
                    <dd className="text-lg font-bold tabular-nums text-gray-900">
                      {formatPrice(c.slice.netSales)}
                    </dd>
                  </div>
                </dl>
              </div>
            ))}

            {/* 합계 — 항상 마지막. 유형 카드가 없으면(구서버) 이것만 남는다. */}
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 sm:col-span-2 lg:col-span-1">
              <p className="text-xs font-semibold text-indigo-700">합계</p>
              <dl className="mt-2 space-y-1 text-sm">
                <div className="flex items-baseline justify-between">
                  <dt className="text-indigo-500">총매출</dt>
                  <dd className="font-medium tabular-nums text-indigo-900">
                    {tier ? formatPrice(tier.grossPayment) : '-'}
                  </dd>
                </div>
                <div className="flex items-baseline justify-between">
                  <dt className="text-indigo-500">취소액</dt>
                  <dd className="font-medium tabular-nums text-indigo-400">
                    {tier?.cancelAmount ? `-${formatPrice(tier.cancelAmount)}` : '-'}
                  </dd>
                </div>
                {showManualRefund && bp.cancelAmountManual > 0 && (
                  <div className="flex items-baseline justify-between text-[11px]">
                    <dt className="text-indigo-400">└ 이체환불</dt>
                    <dd className="tabular-nums text-indigo-400">
                      {`-${formatPrice(bp.cancelAmountManual)}`}
                    </dd>
                  </div>
                )}
                <div className="flex items-baseline justify-between border-t border-indigo-200/70 pt-1.5">
                  <dt className="font-semibold text-indigo-700">순매출</dt>
                  <dd className="text-xl font-extrabold tabular-nums text-indigo-700">
                    {formatPrice(bp.grossSales)}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>

        <div className="mt-2 space-y-1.5 text-xs">
          {/* 상품유형 줄은 한 행에 두 개씩 (쿠폰형 | 배송형). 잔차 `기타`가 있으면 다음 행으로 넘어간다. */}
          <div className="grid gap-x-8 gap-y-1.5 lg:grid-cols-2">
            {typeCols.map((c) => {
              const unit = unitPriceOf(c.slice);
              return (
                <p key={c.key} className="text-gray-400">
                  <span className="font-medium text-gray-600">{c.label}</span>
                  <span className="mx-1.5 text-gray-300">·</span>
                  구성비{' '}
                  <span className="font-medium tabular-nums text-gray-600">
                    {fmtPercent(shareOf(c.slice))}
                  </span>
                  {!!c.slice.netQuantity && (
                    <>
                      <span className="mx-1.5 text-gray-300">·</span>
                      순수량{' '}
                      <span className="font-medium tabular-nums text-gray-600">
                        {c.slice.netQuantity}개
                      </span>
                      {!!c.slice.cancelQuantity && ` (취소 ${c.slice.cancelQuantity}개)`}
                    </>
                  )}
                  {unit && (
                    <>
                      <span className="mx-1.5 text-gray-300">·</span>
                      평균 단가{' '}
                      <span className="font-medium tabular-nums text-gray-600">
                        {formatPrice(unit)}원
                      </span>
                    </>
                  )}
                </p>
              );
            })}
          </div>

          <dl className="flex flex-wrap gap-x-6 gap-y-1.5 pt-1">
            {qtyTotals && (
              <div className="flex gap-1.5">
                <dt className="text-gray-400">총 순수량</dt>
                <dd className="font-medium tabular-nums text-gray-600">
                  {qtyTotals.netQuantity}개
                  {!!totalUnitPrice && (
                    <span className="ml-1 font-normal text-gray-400">
                      (평균 {formatPrice(totalUnitPrice)}원)
                    </span>
                  )}
                </dd>
              </div>
            )}
            {avgOrderValue != null && (
              <div className="flex gap-1.5">
                <dt className="text-gray-400">객단가</dt>
                <dd className="font-medium tabular-nums text-gray-600">
                  {formatPrice(avgOrderValue)}원
                </dd>
              </div>
            )}
            {cancelRateByAmount != null && (
              <div className="flex gap-1.5">
                <dt className="text-gray-400">취소율 (금액)</dt>
                <dd className="font-medium tabular-nums text-gray-600">
                  {fmtPercent(cancelRateByAmount)}
                </dd>
              </div>
            )}
            {cancelRateByQuantity != null && (
              <div className="flex gap-1.5">
                <dt className="text-gray-400">취소율 (수량)</dt>
                <dd className="font-medium tabular-nums text-gray-600">
                  {fmtPercent(cancelRateByQuantity)}
                </dd>
              </div>
            )}
            <div className="flex gap-1.5">
              <dt className="text-gray-400">면세</dt>
              <dd className="font-medium tabular-nums text-gray-600">
                {formatPrice(bp.netTaxFreeSales)}원
                <span className="ml-1 font-normal text-gray-400">
                  ({fmtPercent(taxFreeShare)})
                </span>
              </dd>
            </div>
            <div className="flex gap-1.5">
              <dt className="text-gray-400">과세</dt>
              <dd className="font-medium tabular-nums text-gray-600">
                {formatPrice(bp.netTaxableSales)}원
                <span className="ml-1 font-normal text-gray-400">(VAT 포함)</span>
              </dd>
            </div>
            {bp.serviceFee > 0 && (
              <div className="flex gap-1.5">
                <dt className="text-gray-400">수수료</dt>
                <dd className="font-medium tabular-nums text-gray-900">
                  {formatPrice(bp.serviceFee)}원
                  <span className="ml-1 font-normal text-gray-400">
                    (순매출 × 평균 {fmtPercent(effectiveFeeRate)})
                  </span>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </section>

      {/* 이행 현황 — 위 매출이 정산까지 얼마나 왔는지. 백엔드 필드가 없으면 빈 틀만 그린다. */}
      <DashboardFulfillmentSection
        fulfillment={data.fulfillmentByType}
        salesByType={data.salesByType}
      />
    </div>
  );
};
